/**
 * Analytics (F15).
 *
 * The full picture the pipeline can answer today: stage-to-stage conversion,
 * median days per stage (reconstructed server-side from the audit trail), pilot
 * success rate and portfolio spend, the technology mix, source effectiveness
 * and the rejection-reason distribution.
 *
 * Two presentation rules run through it, both from §4.2 and PRD §9.7:
 *
 *   - **Green is for data values only.** The headline figures (success rate,
 *     spend) carry the green because the number IS the datum; every bar is
 *     coloured by category or by a single neutral accent, never by "how good".
 *   - **A ratio with no base reads "—", not "0%".** The API returns null where
 *     there is no denominator, and this page renders that gap honestly rather
 *     than inventing a zero.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 10.1, PRD F15, §4.2
 */
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { Analytics } from '@nexus/contracts';

import { STAGE_COLOR } from '../../components/domain/stages';
import { Card, CardBody, CardHead } from '../../components/ui/Card';
import { Empty } from '../../components/ui/Empty';
import { FunnelRow } from '../../components/ui/FunnelRow';
import { Kicker } from '../../components/ui/Kicker';
import { Note } from '../../components/ui/Note';
import { PageHead } from '../../components/ui/PageHead';
import { Stat } from '../../components/ui/Stat';
import { useAnalytics } from '../../lib/analytics.queries';
import { formatMoney, formatNumber, formatPercent } from '../../lib/format';
import { ROUTES } from '../../lib/routes';

import { MixList } from './MixList';

/** The seven funnel stages, in order — parked and rejected sit outside the chain. */
const FUNNEL_STAGES = [
  'SCOUTED',
  'SCREENED',
  'QUALIFIED',
  'PILOT_SCOPING',
  'PILOT_LIVE',
  'VALIDATED',
  'COMMERCIAL_ROLLOUT',
] as const;

export function AnalyticsPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { data, isPending, isError } = useAnalytics();

  if (isError) {
    return (
      <p role="alert" className="text-sem-bad">
        {t('errors.generic')}
      </p>
    );
  }

  return (
    <>
      <PageHead
        kicker={t('nav.group.insight')}
        title={t('analytics.title')}
        lead={t('analytics.lead')}
        actions={
          <Link to={ROUTES.reportQuarterly} className="text-blue text-sm hover:underline">
            {t('analytics.quarterlyReport')}
          </Link>
        }
      />

      {isPending ? (
        <p className="text-body" aria-busy="true">
          {t('state.loading')}
        </p>
      ) : (
        <AnalyticsContent data={data} />
      )}
    </>
  );
}

export function AnalyticsContent({ data }: { data: Analytics }): React.JSX.Element {
  const { t } = useTranslation();

  const counts = new Map(data.conversion.map((row) => [row.stage, row.count]));
  const funnelRows = FUNNEL_STAGES.map((stage) => ({
    stage,
    count: counts.get(stage) ?? 0,
  }));
  const max = funnelRows.reduce((m, row) => Math.max(m, row.count), 0);
  const total = funnelRows.reduce((sum, row) => sum + row.count, 0);

  /* Cents (string, BigInt at the API boundary) into whole euros: the UI's money
     formatter works in euros, and Number is safe here because a pilot budget is
     nowhere near 2^53 cents. */
  const spendEuros = Number(data.spendCents) / 100;

  return (
    <div className="flex flex-col gap-s4">
      <div className="grid grid-cols-1 gap-s3 sm:grid-cols-2">
        <Card>
          <Stat
            label={t('analytics.pilots.successRate')}
            value={
              data.pilots.successRate === null
                ? '—'
                : formatPercent(data.pilots.successRate, 1)
            }
            hint={t('analytics.pilots.successHint', {
              success: data.pilots.success,
              concluded: data.pilots.success + data.pilots.partial + data.pilots.failed,
            })}
          />
        </Card>
        <Card>
          <Stat
            label={t('analytics.spend.title')}
            value={spendEuros === 0 ? '—' : formatMoney(spendEuros, 'exact')}
            hint={t('analytics.spend.hint')}
          />
        </Card>
      </div>

      <Card>
        <CardHead>
          <Kicker as="h2">{t('analytics.conversion.title')}</Kicker>
        </CardHead>
        <CardBody>
          {total === 0 ? (
            <Empty
              title={t('analytics.conversion.emptyTitle')}
              action={
                <Link to={ROUTES.startups} className="text-blue text-sm hover:underline">
                  {t('dashboard.goToStartups')}
                </Link>
              }
            >
              {t('analytics.conversion.emptyBody')}
            </Empty>
          ) : (
            funnelRows.map((row, index) => {
              const next = funnelRows[index + 1];
              const conversion =
                next !== undefined && row.count > 0
                  ? formatPercent((next.count / row.count) * 100)
                  : undefined;

              return (
                <FunnelRow
                  key={row.stage}
                  label={t(`stage.${row.stage}`)}
                  count={row.count}
                  max={max}
                  color={STAGE_COLOR[row.stage]}
                  conversion={conversion}
                />
              );
            })
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHead>
          <Kicker as="h2">{t('analytics.durations.title')}</Kicker>
        </CardHead>
        <CardBody>
          {data.stageDurations.every((row) => row.medianDays === null) ? (
            <Empty title={t('analytics.durations.emptyTitle')}>
              {t('analytics.durations.emptyBody')}
            </Empty>
          ) : (
            <ul className="flex flex-col divide-y divide-gray-100">
              {data.stageDurations.map((row) => (
                <li key={row.stage} className="flex items-center justify-between py-2">
                  <span className="text-body text-sm font-light">
                    {t(`stage.${row.stage}`)}
                  </span>
                  <span className="font-display text-navy-text text-sm font-extrabold tabular-nums">
                    {row.medianDays === null
                      ? '—'
                      : t('analytics.durations.days', {
                          days: formatNumber(row.medianDays, { maximumFractionDigits: 1 }),
                          count: row.sampleSize,
                        })}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <div className="grid grid-cols-1 gap-s3 lg:grid-cols-2">
        <Card>
          <CardHead>
            <Kicker as="h2">{t('analytics.techMix.title')}</Kicker>
          </CardHead>
          <CardBody>
            <MixList
              rows={data.technologyMix.map((row) => ({
                key: row.key,
                label: row.label,
                value: row.count,
                caption: t('analytics.techMix.uses', { count: row.count }),
              }))}
              emptyTitle={t('analytics.techMix.emptyTitle')}
              emptyBody={t('analytics.techMix.emptyBody')}
            />
          </CardBody>
        </Card>

        <Card>
          <CardHead>
            <Kicker as="h2">{t('analytics.rejections.title')}</Kicker>
          </CardHead>
          <CardBody>
            <MixList
              rows={data.rejectionDistribution.map((row) => ({
                key: row.key,
                label:
                  row.key === 'unspecified'
                    ? t('analytics.rejections.unspecified')
                    : row.label,
                value: row.count,
                caption: t('analytics.rejections.count', { count: row.count }),
              }))}
              emptyTitle={t('analytics.rejections.emptyTitle')}
              emptyBody={t('analytics.rejections.emptyBody')}
            />
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHead>
          <Kicker as="h2">{t('analytics.sources.title')}</Kicker>
        </CardHead>
        <CardBody>
          {data.sourceEffectiveness.length === 0 ? (
            <Empty title={t('analytics.sources.emptyTitle')}>
              {t('analytics.sources.emptyBody')}
            </Empty>
          ) : (
            <ul className="flex flex-col divide-y divide-gray-100">
              {data.sourceEffectiveness.map((row) => (
                <li key={row.key} className="flex items-center justify-between py-2">
                  <span className="text-body text-sm font-light">{row.label}</span>
                  <span className="text-body text-xs font-light">
                    {t('analytics.sources.detail', {
                      reached: row.reachedPilot,
                      total: row.total,
                    })}
                    <span className="font-display text-navy-text ml-3 text-sm font-extrabold tabular-nums">
                      {row.pilotRate === null ? '—' : formatPercent(row.pilotRate, 0)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <Note>{t('analytics.footnote')}</Note>
    </div>
  );
}
